import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Megaphone, ImagePlus, X, CheckCircle2, Loader2, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, uploadFile } from "@/api/client";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";

const inputCls = "w-full rounded-lg border border-line bg-white px-3 py-2.5 text-[15px] text-ink outline-none focus:border-brand";

const STATUS_COLORS = {
  received: "bg-brand-tint text-brand",
  in_review: "bg-[#FFF3E0] text-[#E65100]",
  resolved: "bg-[#E8F5E9] text-[#2E7D32]",
};

export default function Grievance() {
  const { t } = useI18n();
  const CATS = [
    ["catLand", t("catLand")], ["catCorruption", t("catCorruption")], ["catPublic", t("catPublic")],
    ["catWomen", t("catWomen")], ["catCommunal", t("catCommunal")], ["catOther", t("catOther")],
  ];
  const [form, setForm] = useState({ subject: "", category: t("catLand"), location: "", description: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [memberLoading, setMemberLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let active = true;
    api("/membership/me")
      .then((r) => { if (active) setIsMember(r?.member?.status === "active"); })
      .catch(() => { if (active) setIsMember(false); })
      .finally(() => { if (active) setMemberLoading(false); });
    return () => { active = false; };
  }, []);

  const loadMine = useCallback(
    () => api("/complaints/mine").then(setComplaints).catch((e) => console.error("Failed to load complaints", e)),
    []
  );
  useEffect(() => { loadMine(); }, [loadMine]);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - photos.length);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const { path } = await uploadFile(f, "/upload/photo");
        setPhotos((p) => [...p, path]);
      }
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error(t("grvFillReq"));
      return;
    }
    setBusy(true);
    try {
      const r = await api("/complaints", {
        method: "POST",
        body: JSON.stringify({
          subject: form.subject.trim(),
          category: form.category,
          location: form.location.trim() || null,
          description: form.description.trim(),
          photo_paths: photos,
        }),
      });
      setDone(r.ticket_id);
      setForm({ subject: "", category: t("catLand"), location: "", description: "" });
      setPhotos([]);
      loadMine();
    } catch (err) {
      toast.error(err.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <CheckCircle2 className="mx-auto h-16 w-16 text-[#2E7D32]" />
        <h1 className="mt-4 text-2xl font-black text-ink">{t("grvOkTitle")}</h1>
        <p className="mt-2 text-ink-muted">{t("grvOkSub")}</p>
        <div className="mt-4 inline-block rounded-full bg-brand-tint px-5 py-2 font-bold text-brand">{t("grvTicket")}: {done}</div>
        <div className="mt-6 flex flex-col gap-3">
          <Link to={`/complaint/${done}`}><Button className="h-11 w-full rounded-full bg-brand font-bold hover:bg-brand-dark">{t("trackTitle")}</Button></Link>
          <Button variant="outline" onClick={() => setDone(null)} className="h-11 w-full rounded-full font-bold">{t("grvAnother")}</Button>
        </div>
      </div>
    );
  }

  if (memberLoading) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!isMember) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="grievance-blocked">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-tint text-brand">
          <Megaphone size={30} />
        </span>
        <h1 className="mt-5 text-2xl font-black text-ink">{t("grvMembersOnlyTitle")}</h1>
        <p className="mt-2 text-ink-muted">{t("grvMembersOnlyBody")}</p>
        <Link to="/register" className="mt-6 inline-block">
          <Button data-testid="grievance-become-member" className="h-11 rounded-full bg-brand px-8 font-bold hover:bg-brand-dark">
            {t("grvBecomeMember")}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-tint text-brand"><Megaphone size={22} /></span>
        <div>
          <h1 className="text-2xl font-black text-ink">{t("grvTitle")}</h1>
          <p className="text-sm text-ink-muted">{t("grvSubtitle")}</p>
        </div>
      </div>

      <div className="mt-8 space-y-5 rounded-3xl border border-line bg-white p-6 sm:p-8">
        <Field label={t("grvSubject")}>
          <input className={inputCls} value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder={t("grvSubjectPh")} data-testid="grv-subject" />
        </Field>
        <Field label={t("grvCategory")}>
          <select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="grv-category">
            {CATS.map(([k, label]) => <option key={k} value={label}>{label}</option>)}
          </select>
        </Field>
        <Field label={t("grvLocation")}>
          <input className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder={t("grvLocationPh")} data-testid="grv-location" />
        </Field>
        <Field label={t("grvDesc")}>
          <textarea className={`${inputCls} min-h-[110px]`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder={t("grvDescPh")} data-testid="grv-description" />
        </Field>
        <Field label={t("grvPhotos")}>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={addPhotos} />
          <div className="flex flex-wrap gap-3">
            {photos.map((p, i) => (
              <div key={p} className="relative flex h-20 w-20 items-center justify-center rounded-lg border border-line bg-brand-tint">
                <CheckCircle2 className="text-[#2E7D32]" />
                <button onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#C62828] text-white"><X size={12} /></button>
              </div>
            ))}
            {photos.length < 3 && (
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="grv-add-photo" className="flex h-20 w-20 items-center justify-center rounded-lg border-2 border-dashed border-brand/40 text-brand">
                {uploading ? <Loader2 className="animate-spin" /> : <ImagePlus size={22} />}
              </button>
            )}
          </div>
        </Field>
        <Button data-testid="grv-submit" onClick={submit} disabled={busy} className="h-12 w-full rounded-full bg-brand text-base font-bold hover:bg-brand-dark">
          {busy ? <Loader2 className="animate-spin" /> : t("grvSubmit")}
        </Button>
      </div>

      {complaints.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-ink-muted">{t("profComplaints")}</h2>
          <div className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white">
            {complaints.map((c) => (
              <Link key={c.ticket_id} to={`/complaint/${c.ticket_id}`} data-testid={`grv-item-${c.ticket_id}`} className="flex items-center gap-3 p-4 hover:bg-brand-tint/40">
                <FileText size={18} className="text-brand" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-ink">{c.subject}</div>
                  <div className="text-xs text-ink-muted">{c.ticket_id}</div>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${STATUS_COLORS[c.status] || STATUS_COLORS.received}`}>
                  {c.status === "resolved" ? t("stResolved") : c.status === "in_review" ? t("stInReview") : t("stReceived")}
                </span>
                <ChevronRight size={16} className="text-ink-muted" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-ink-soft">{label}</label>
      {children}
    </div>
  );
}
